import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  MapPin, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Navigation,
  Clock,
  Power,
  PowerOff
} from "lucide-react";
import { Button } from "../components/ui/button";
import { API_URL } from "../config/api";


export default function DriverContinuousTrackingPage() {
  const { orderId, token } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "single"; // "single" or "continuous"
  const endDate = searchParams.get("end"); // End date for continuous tracking
  
  const [status, setStatus] = useState("idle"); // idle, requesting, active, success, error, stopped
  const [errorMessage, setErrorMessage] = useState("");
  const [locationData, setLocationData] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [trackingActive, setTrackingActive] = useState(false);
  const [watchId, setWatchId] = useState(null);

  const formatEndDate = () => {
    if (!endDate) return null;
    try {
      return new Date(endDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return endDate;
    }
  };

  const sendLocation = useCallback(async (latitude, longitude, accuracy) => {
    try {
      const response = await fetch(
        `${API_URL}/track/${orderId}/${token}/submit?latitude=${latitude}&longitude=${longitude}&accuracy=${accuracy || 0}&continuous=${mode === "continuous"}`,
        { 
          method: "POST",
          headers: { "Content-Type": "application/json" }
        }
      );

      if (response.ok) {
        setLocationData({ latitude, longitude, accuracy });
        setLastUpdate(new Date().toISOString());
        setUpdateCount(prev => prev + 1);
        return true;
      } else {
        const data = await response.json();
        throw new Error(data.detail || "Failed to submit location");
      }
    } catch (error) {
      console.error("Location send error:", error);
      return false;
    }
  }, [orderId, token, mode]);

  const requestSingleLocation = async () => {
    setStatus("requesting");
    setErrorMessage("");

    if (!navigator.geolocation) {
      setStatus("error");
      setErrorMessage("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const success = await sendLocation(latitude, longitude, accuracy);
        setStatus(success ? "success" : "error");
        if (!success) setErrorMessage("Failed to submit location");
      },
      (error) => {
        setStatus("error");
        handleLocationError(error);
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );
  };

  const startContinuousTracking = () => {
    if (!navigator.geolocation) {
      setStatus("error");
      setErrorMessage("Geolocation is not supported by your browser");
      return;
    }

    setStatus("active");
    setTrackingActive(true);

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        await sendLocation(latitude, longitude, accuracy);
      },
      (error) => handleLocationError(error),
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );

    // Watch position for continuous updates
    const id = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        await sendLocation(latitude, longitude, accuracy);
      },
      (error) => {
        console.error("Watch position error:", error);
        // Don't stop tracking on individual errors
      },
      { enableHighAccuracy: true, maximumAge: 60000 } // Allow 1 min cached position
    );

    setWatchId(id);

    // Also send updates every hour as backup
    const intervalId = setInterval(async () => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          await sendLocation(latitude, longitude, accuracy);
        },
        () => {}, // Ignore errors for interval updates
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
      );
    }, 60 * 60 * 1000); // Hourly

    // Store interval ID for cleanup
    window.trackingIntervalId = intervalId;
  };

  const stopContinuousTracking = () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    if (window.trackingIntervalId) {
      clearInterval(window.trackingIntervalId);
      window.trackingIntervalId = null;
    }
    setTrackingActive(false);
    setStatus("stopped");
  };

  const handleLocationError = (error) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        setErrorMessage("Location permission denied. Please allow location access and try again.");
        break;
      case error.POSITION_UNAVAILABLE:
        setErrorMessage("Location unavailable. Please check your GPS settings.");
        break;
      case error.TIMEOUT:
        setErrorMessage("Location request timed out. Please try again.");
        break;
      default:
        setErrorMessage("Unable to get location. Please try again.");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
      if (window.trackingIntervalId) {
        clearInterval(window.trackingIntervalId);
      }
    };
  }, [watchId]);

  // Check if we should auto-start continuous tracking
  useEffect(() => {
    if (mode === "single") {
      // Auto-request for single mode
      const timer = setTimeout(requestSingleLocation, 500);
      return () => clearTimeout(timer);
    }
  }, [mode]);

  const renderModeSelection = () => (
    <div className="space-y-6">
      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
        <MapPin className="w-10 h-10 text-blue-600" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Share Your Location</h2>
        <p className="text-gray-600 text-sm">
          Choose how you'd like to share your location with Europacific.
        </p>
      </div>

      <div className="space-y-3">
        <Button
          onClick={requestSingleLocation}
          size="lg"
          className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
          data-testid="share-once-btn"
        >
          <Navigation className="w-5 h-5 mr-2" />
          Share Current Location
        </Button>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        <Button
          onClick={startContinuousTracking}
          size="lg"
          variant="outline"
          className="w-full text-lg py-6 border-green-500 text-green-700 hover:bg-green-50"
          data-testid="share-continuous-btn"
        >
          <Power className="w-5 h-5 mr-2" />
          Share Until Trip Ends
        </Button>
        
        {endDate && (
          <p className="text-center text-sm text-gray-500">
            Continuous tracking ends: {formatEndDate()}
          </p>
        )}
      </div>
    </div>
  );

  const renderActiveTracking = () => (
    <div className="space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto relative">
        <Navigation className="w-10 h-10 text-green-600" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse"></span>
      </div>
      <div>
        <h2 className="text-xl font-semibold text-green-700 mb-2">Tracking Active</h2>
        <p className="text-gray-600 text-sm">
          Your location is being shared with Europacific.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Updates sent:</span>
          <span className="font-semibold text-gray-900">{updateCount}</span>
        </div>
        {lastUpdate && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Last update:</span>
            <span className="font-semibold text-gray-900">
              {new Date(lastUpdate).toLocaleTimeString()}
            </span>
          </div>
        )}
        {locationData && (
          <div className="text-xs text-gray-400 pt-2 border-t">
            <p>Lat: {locationData.latitude.toFixed(5)}</p>
            <p>Lng: {locationData.longitude.toFixed(5)}</p>
          </div>
        )}
      </div>

      {endDate && (
        <p className="text-center text-sm text-gray-500">
          <Clock className="w-4 h-4 inline mr-1" />
          Tracking until: {formatEndDate()}
        </p>
      )}

      <Button
        onClick={stopContinuousTracking}
        size="lg"
        variant="outline"
        className="w-full text-lg py-6 border-red-500 text-red-600 hover:bg-red-50"
        data-testid="stop-tracking-btn"
      >
        <PowerOff className="w-5 h-5 mr-2" />
        Stop Sharing Location
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
      >
        {/* Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-blue-900">Europacific</h1>
          <p className="text-gray-500 text-sm">Driver Location Tracking</p>
        </div>

        {/* Status Display */}
        {status === "idle" && mode !== "single" && renderModeSelection()}

        {status === "idle" && mode === "single" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <MapPin className="w-10 h-10 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Share Your Location</h2>
              <p className="text-gray-600">Tap the button below to share your current location.</p>
            </div>
            <Button
              onClick={requestSingleLocation}
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
            >
              <Navigation className="w-5 h-5 mr-2" />
              Share Location
            </Button>
          </div>
        )}

        {status === "requesting" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Getting Location...</h2>
              <p className="text-gray-600">Please allow location access when prompted.</p>
            </div>
          </div>
        )}

        {status === "active" && renderActiveTracking()}

        {status === "success" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-green-700 mb-2">Location Received!</h2>
              <p className="text-gray-600 mb-4">Thank you! Your location has been sent.</p>
              {locationData && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-500">
                  <p>Lat: {locationData.latitude.toFixed(5)}</p>
                  <p>Lng: {locationData.longitude.toFixed(5)}</p>
                </div>
              )}
            </div>
            <p className="text-gray-500 text-sm">You can close this page now.</p>
          </div>
        )}

        {status === "stopped" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
              <PowerOff className="w-10 h-10 text-gray-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-700 mb-2">Tracking Stopped</h2>
              <p className="text-gray-600 mb-4">
                Location sharing has been stopped. {updateCount} updates were sent.
              </p>
            </div>
            <Button
              onClick={() => { setStatus("idle"); setUpdateCount(0); }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Start Again
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
              <p className="text-gray-600">{errorMessage}</p>
            </div>
            <Button
              onClick={() => { setStatus("idle"); setErrorMessage(""); }}
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t text-xs text-gray-400">
          <p>Europacific Logistika d.o.o.</p>
          <p>+386 5 662 6380 | info@europacific.com</p>
        </div>
        
        {/* GDPR Notice for Driver */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-500 text-left">
          <p className="font-medium text-gray-600 mb-1">Your Rights (GDPR):</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Location sharing is voluntary - you can stop at any time</li>
            <li>Data is used only for delivery tracking per Art. 6(1)(b)(f)</li>
            <li>Location data is deleted after service completion</li>
            <li>Contact privacy@europacific.com for data requests</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}
